import { Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import Overview from '@/components/dashboard/Overview';
import GrowthSnapshot from '@/components/dashboard/GrowthSnapshot';
import Keywords from '@/components/dashboard/Keywords';
import LinkBuilding from '@/components/dashboard/LinkBuilding';
import PositionTracking from '@/components/dashboard/PositionTracking';
import TrafficAnalytics from '@/components/dashboard/TrafficAnalytics';
import Content from '@/components/dashboard/Content';
import Reporting from '@/components/dashboard/Reporting';
import BlogManager from '@/components/dashboard/BlogManager';
import BlogEditor from '@/components/dashboard/BlogEditor';
import SocialMedia from '@/components/dashboard/SocialMedia';
import PaidAds from '@/components/dashboard/PaidAds';
import AIVideo from '@/components/dashboard/AIVideo';
import LegalTranslations from '@/components/dashboard/LegalTranslations';
import WebsiteCreation from '@/components/dashboard/WebsiteCreation';
import EmailMarketing from '@/components/dashboard/EmailMarketing';
import WebDev from '@/components/dashboard/WebDev';
import BillingPlan from '@/components/dashboard/BillingPlan';
import AccessGate, { PastDueBanner, NoPlanBanner } from '@/components/dashboard/AccessGate';
import LockedPreview from '@/components/dashboard/LockedPreview';
import MessagesBubble from '@/components/MessagesBubble';
import DashboardTour from '@/components/dashboard/DashboardTour';
import OnboardingVideo from '@/components/dashboard/OnboardingVideo';
import BrandPrioritiesModal from '@/components/dashboard/BrandPrioritiesModal';
import BrandPrioritiesPage from '@/components/dashboard/BrandPrioritiesPage';
import AccessRequestsPage from '@/components/dashboard/AccessRequestsPage';
import OnboardingDetailsPage from '@/components/dashboard/OnboardingDetailsPage';
import { ArrowLeft } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Menu } from 'lucide-react';
import axios from 'axios';
import { computeAccessState } from '@/lib/accessState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard({ user, onLogout, selectedClient, onSwitchClient }) {
  // Determine if viewing as admin/internal team
  const isViewingAsAdmin = selectedClient && user.role !== 'client';
  const displayName = isViewingAsAdmin ? selectedClient.name : user.firm?.name;

  // Plan config: client uses own firm; admin/team viewing a client fetches it.
  const [planConfig, setPlanConfig] = useState(user.firm?.plan_config || null);
  useEffect(() => {
    const targetFirmId = selectedClient?.firmId || (user.role === 'client' ? user.firm?.id : null);
    if (!targetFirmId) { setPlanConfig(null); return; }
    if (user.role === 'client') { setPlanConfig(user.firm?.plan_config || null); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/firms/${targetFirmId}/plan-config`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setPlanConfig(res.data);
      } catch (e) { console.error(e); setPlanConfig(null); }
    })();
  }, [selectedClient, user]);

  // Billing + access-state gate for clients only
  const [billing, setBilling] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [access, setAccess] = useState(user.role === 'client' ? { state: 'loading' } : { state: 'full_access' });
  useEffect(() => {
    if (user.role !== 'client') { setAccess({ state: 'full_access' }); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/me/billing`, { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        setBilling(res.data);
        setAccess(computeAccessState(res.data));
        // Keep plan_config fresh from the source of truth
        if (res.data?.plan_config) setPlanConfig(res.data.plan_config);
      } catch (_e) {
        // Fail open - don't lock the user out if /me/billing is unreachable
        if (!cancelled) setAccess({ state: 'full_access' });
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const chatEnabled = !!(planConfig?.chat_enabled ?? planConfig?.account_manager_enabled ?? true);

  // Brand & Priorities intake gate (clients on an active core SEO plan only)
  const CORE_PLANS = ['taster', 'growth', 'authority'];
  const [brandNeeded, setBrandNeeded] = useState(false);
  const [brandSkipped, setBrandSkipped] = useState(false);
  // Sequence after the onboarding video so the two blocking modals never collide
  const [videoDone, setVideoDone] = useState(() => {
    try { return localStorage.getItem('la360_onboarding_video_v1') === '1'; } catch (_e) { return true; }
  });
  useEffect(() => {
    if (user.role !== 'client' || !user.firm?.id) { setBrandNeeded(false); return; }
    const planType = (planConfig?.plan_type || '').toLowerCase();
    if (!CORE_PLANS.includes(planType)) { setBrandNeeded(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/brand-priorities/${user.firm.id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setBrandNeeded(!res.data?.submitted);
      } catch (_e) { if (!cancelled) setBrandNeeded(false); }
    })();
    return () => { cancelled = true; };
  }, [user, planConfig]);

  // `no_plan` no longer locks the dashboard - clients keep full nav and see a banner instead.
  // Hard-restrict only on cancelled or past-due-locked states.
  const restricted = access.state === 'cancelled' || access.state === 'past_due_locked';
  const showGraceBanner = access.state === 'past_due_grace';
  const showNoPlanBanner = access.state === 'no_plan';
  // Blur core tabs (overview, keywords, link-building, position-tracking,
  // traffic-analytics, content, reporting) for unpaid clients only.
  // Add-ons & Legal Translations are NOT blurred - they have their own paywalls.
  const showCoreLockedPreview = user.role === 'client' && access.state === 'no_plan';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-slate-50">
      <Sidebar 
        user={user} 
        onLogout={onLogout} 
        clientName={displayName}
        isViewingAsAdmin={isViewingAsAdmin}
        onSwitchClient={onSwitchClient}
        planConfig={planConfig}
        restricted={restricted}
        mobileOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      
      <main className="p-4 lg:p-8 overflow-y-auto min-w-0" data-testid="dashboard-main">
        {/* Mobile top bar with hamburger */}
        <div className="lg:hidden flex items-center gap-3 mb-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2.5 rounded-xl bg-slate-900 text-white shadow-sm"
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <span className="font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Law Agency 360</span>
        </div>

        {/* Admin Context Banner */}
        {isViewingAsAdmin && (
          <div className="mb-6 bg-gradient-to-r from-violet-500/10 to-indigo-500/10 backdrop-blur-sm border border-violet-200/50 rounded-2xl p-5 flex items-center justify-between soft-shadow animate-fadeInUp" data-testid="admin-context-banner">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/30">
                <span className="text-white font-bold text-lg">
                  {selectedClient.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="font-semibold text-slate-900 text-lg">Viewing: {selectedClient.name}</div>
                <div className="text-sm text-slate-500">
                  {user.role === 'super_admin' ? 'Super Admin View' : 'Team Member View'}
                </div>
              </div>
            </div>
            <button
              onClick={onSwitchClient}
              className="flex items-center gap-2 px-5 py-2.5 bg-white/80 backdrop-blur-sm border border-slate-200/50 text-slate-700 hover:text-violet-600 rounded-xl transition-all duration-300 hover:shadow-lg font-medium"
              data-testid="switch-client-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              {user.role === 'super_admin' ? 'Back to Admin Panel' : 'Switch Client'}
            </button>
          </div>
        )}

        {showGraceBanner && <PastDueBanner billing={billing} graceDaysLeft={access.grace_days_left} />}
        {showNoPlanBanner && <NoPlanBanner />}

        {access.state === 'loading' ? (
          <div className="p-8 text-slate-400" data-testid="dashboard-loading">Loading your dashboard…</div>
        ) : (
          <Routes>
            {/* Billing is always reachable for clients so they can recover their plan */}
            <Route path="/billing" element={<BillingPlan />} />
            {restricted ? (
              <Route path="*" element={<AccessGate state={access.state} billing={billing} />} />
            ) : (
              <>
                <Route path="/" element={<Navigate to="/dashboard/growth-snapshot" replace />} />
                <Route path="/growth-snapshot" element={<GrowthSnapshot user={user} selectedClient={selectedClient} />} />
                <Route path="/brand-priorities" element={<BrandPrioritiesPage user={user} selectedClient={selectedClient} />} />
                <Route path="/access" element={<AccessRequestsPage user={user} selectedClient={selectedClient} />} />
                <Route path="/onboarding-details" element={<OnboardingDetailsPage user={user} selectedClient={selectedClient} />} />
                <Route path="/overview" element={<LockedPreview active={showCoreLockedPreview}><Overview user={user} selectedClient={selectedClient} /></LockedPreview>} />
                <Route path="/keywords" element={<LockedPreview active={showCoreLockedPreview}><Keywords user={user} selectedClient={selectedClient} /></LockedPreview>} />
                <Route path="/link-building" element={<LockedPreview active={showCoreLockedPreview}><LinkBuilding user={user} selectedClient={selectedClient} /></LockedPreview>} />
                <Route path="/position-tracking" element={<LockedPreview active={showCoreLockedPreview}><PositionTracking user={user} selectedClient={selectedClient} /></LockedPreview>} />
                <Route path="/traffic-analytics" element={<LockedPreview active={showCoreLockedPreview}><TrafficAnalytics user={user} selectedClient={selectedClient} /></LockedPreview>} />
                <Route path="/content" element={<LockedPreview active={showCoreLockedPreview}><Content user={user} selectedClient={selectedClient} /></LockedPreview>} />
                <Route path="/reporting" element={<LockedPreview active={showCoreLockedPreview}><Reporting user={user} selectedClient={selectedClient} /></LockedPreview>} />
                <Route path="/blogs" element={<BlogManager user={user} />} />
                <Route path="/blogs/new" element={<BlogEditor user={user} />} />
                <Route path="/blogs/edit/:blogId" element={<BlogEditor user={user} />} />
                <Route path="/social-media" element={<SocialMedia user={user} selectedClient={selectedClient} />} />
                <Route path="/paid-ads" element={<PaidAds user={user} selectedClient={selectedClient} />} />
                <Route path="/ai-video" element={<AIVideo user={user} selectedClient={selectedClient} />} />
                <Route path="/website-creation" element={<WebsiteCreation user={user} selectedClient={selectedClient} />} />
                <Route path="/email-marketing" element={<EmailMarketing user={user} selectedClient={selectedClient} />} />
                <Route path="/web-dev" element={<WebDev user={user} selectedClient={selectedClient} planConfig={planConfig} />} />
                <Route path="/legal-translations" element={<LegalTranslations user={user} selectedClient={selectedClient} />} />
              </>
            )}
          </Routes>
        )}
      </main>
      {chatEnabled && !restricted && <MessagesBubble user={user} />}
      {!restricted && access.state !== 'loading' && <DashboardTour user={user} />}
      {!restricted && access.state !== 'loading' && <OnboardingVideo user={user} onClose={() => setVideoDone(true)} />}
      {!restricted && access.state !== 'loading' && videoDone && brandNeeded && !brandSkipped && (
        <BrandPrioritiesModal
          firmId={user.firm.id}
          onSubmitted={() => { setBrandNeeded(false); }}
          onSkip={() => { setBrandSkipped(true); }}
        />
      )}
    </div>
  );
}
